import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/AuthContext';
import api, { fetchAuditLog, fetchAuditSummary, fetchPermissionMatrix, updateRolePermissions, fetchUserPermissions, updateUserOverrides, clearUserOverrides } from '@/lib/api';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { UserAvatar } from '@/components/common/UserAvatar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Users, Shield, Check, X, ScrollText, ShieldCheck, LogIn, AlertTriangle } from 'lucide-react';

const ROLES = [
  { key: 'admin', label: 'Administrador', color: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'direccion', label: 'Dirección', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { key: 'comercial', label: 'Comercial', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'obra', label: 'Obra', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'finanzas', label: 'Finanzas', color: 'bg-blue-50 text-blue-700 border-blue-200' },
];

const roleColor = (r) => ROLES.find(x => x.key === r)?.color || '';
const roleLabel = (r) => ROLES.find(x => x.key === r)?.label || r;

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [overrideTarget, setOverrideTarget] = useState(null);

  const load = async () => {
    try { const res = await api.get('/auth/users'); setUsers(res.data); }
    catch { toast.error('Error al cargar usuarios'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    try { await api.delete(`/auth/users/${id}`); setUsers(us => us.filter(u => u.id !== id)); toast.success('Usuario eliminado'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-[300px] rounded-xl" /></div>;

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <PageHeader title="Usuarios" subtitle={`${users.length} usuarios registrados`}>
        <Button onClick={() => setCreateOpen(true)} data-testid="users-add-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo Usuario
        </Button>
      </PageHeader>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users"><Users className="h-3.5 w-3.5 mr-1.5" />Usuarios</TabsTrigger>
          <TabsTrigger value="permissions"><Shield className="h-3.5 w-3.5 mr-1.5" />Permisos</TabsTrigger>
          <TabsTrigger value="audit"><ScrollText className="h-3.5 w-3.5 mr-1.5" />Bitácora</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
      <Card className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4">Nombre</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4">Rol</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4">Estado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4">MFA</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} className="table-row-hover group">
                <TableCell className="py-2.5 px-4">
                  <div className="flex items-center gap-2.5">
                    <UserAvatar user={u} name={u.name} email={u.email} size="sm" />
                    <span className="text-sm font-medium">{u.name}</span>
                  </div>
                </TableCell>
                <TableCell className="py-2.5 px-4 text-sm text-slate-500">{u.email}</TableCell>
                <TableCell className="py-2.5 px-4"><Badge variant="outline" className={`${roleColor(u.role)} text-xs`}>{roleLabel(u.role)}</Badge></TableCell>
                <TableCell className="py-2.5 px-4">
                  <Badge variant="outline" className={u.active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-xs' : 'bg-slate-50 text-slate-500 border-slate-200 text-xs'}>
                    {u.active !== false ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5 px-4">
                  <Badge variant="outline" className={`text-[9px] ${u.mfa_enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                    {u.mfa_enabled ? 'MFA Activo' : 'Sin MFA'}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5 px-4">
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-[#F05A28]" title="Permisos individuales" onClick={() => setOverrideTarget(u)}><Shield className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => setEditTarget(u)}><Pencil className="h-3.5 w-3.5" /></Button>
                    {u.id !== currentUser?.id && <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => handleDelete(u.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Permissions matrix */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Shield className="h-4 w-4 text-[#F05A28]" /> Permisos por Rol</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Los permisos se definen por rol. Para cambiar permisos de un usuario, cambia su rol.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-4">Acción</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-red-500 font-semibold py-2 px-4 text-center w-28">Admin</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-blue-500 font-semibold py-2 px-4 text-center w-28">Editor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-4 text-center w-28">Visor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              { action: 'Ver Dashboard, Viviendas, Egresos, Crédito, Préstamos, Flujo, Avance', admin: true, editor: true, visor: true },
              { action: 'Crear / Editar viviendas, pagos, gastos', admin: true, editor: true, visor: false },
              { action: 'Eliminar registros', admin: true, editor: true, visor: false },
              { action: 'Configuración del proyecto', admin: true, editor: true, visor: false },
              { action: 'Catálogo y plan de obra', admin: true, editor: true, visor: false },
              { action: 'Gestionar usuarios', admin: true, editor: false, visor: false },
            ].map((row, i) => (
              <TableRow key={i} className="table-row-hover">
                <TableCell className="py-2 px-4 text-xs">{row.action}</TableCell>
                <TableCell className="py-2 px-4 text-center">{row.admin ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-200 mx-auto" />}</TableCell>
                <TableCell className="py-2 px-4 text-center">{row.editor ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-200 mx-auto" />}</TableCell>
                <TableCell className="py-2 px-4 text-center">{row.visor ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <X className="h-4 w-4 text-slate-200 mx-auto" />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="permissions">
          <PermissionsMatrix />
        </TabsContent>

        <TabsContent value="audit">
          <AuditLogView />
        </TabsContent>
      </Tabs>

      <UserModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />
      {editTarget && <UserModal open={true} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} initial={editTarget} />}
      {overrideTarget && <UserOverrideDialog user={overrideTarget} onClose={() => setOverrideTarget(null)} />}
    </div>
  );
}

function UserModal({ open, onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || '',
    email: initial?.email || '',
    password: '',
    role: initial?.role || 'visor',
    active: initial?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const updates = { name: form.name, email: form.email, role: form.role, active: form.active };
        if (form.password) updates.password = form.password;
        await api.put(`/auth/users/${initial.id}`, updates);
      } else {
        await api.post('/auth/users', form);
      }
      toast.success(isEdit ? 'Usuario actualizado' : 'Usuario creado');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-[#F05A28]" />{isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Nombre</label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre completo" /></div>
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@ejemplo.com" /></div>
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">{isEdit ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}</label><Input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={isEdit ? 'Sin cambios' : 'Mínimo 6 caracteres'} /></div>
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Rol</label>
            <Select value={form.role} onValueChange={v => set('role', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose><Button onClick={handleSave} disabled={!form.name || !form.email || (!isEdit && !form.password) || saving}>{saving ? 'Guardando...' : 'Guardar'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_LABELS = {
  login_success: { label: 'Login exitoso', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: '✓' },
  login_failed: { label: 'Login fallido', cls: 'bg-red-50 text-red-600 border-red-200', icon: '✗' },
  logout: { label: 'Cierre de sesión', cls: 'bg-slate-100 text-slate-600 border-slate-300', icon: '→' },
  mfa_failed: { label: 'MFA fallido', cls: 'bg-red-50 text-red-600 border-red-200', icon: '✗' },
  mfa_activated: { label: 'MFA activado', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: '🔐' },
  mfa_disabled: { label: 'MFA desactivado', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: '🔓' },
  mfa_admin_reset: { label: 'MFA reset (admin)', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: '🔄' },
  password_changed: { label: 'Contraseña cambiada', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: '🔑' },
  password_change_failed: { label: 'Cambio contraseña fallido', cls: 'bg-red-50 text-red-600 border-red-200', icon: '✗' },
  user_created: { label: 'Usuario creado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: '+' },
  user_updated: { label: 'Usuario editado', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: '✎' },
  user_deleted: { label: 'Usuario eliminado', cls: 'bg-red-50 text-red-600 border-red-200', icon: '−' },
  access_denied: { label: 'Acceso denegado', cls: 'bg-red-50 text-red-600 border-red-200', icon: '⛔' },
};
const FAILED_ACTIONS = new Set(['login_failed','mfa_failed','password_change_failed','access_denied']);

function AuditLogView() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filterAction !== 'all') params.action = filterAction;
      if (filterEmail) params.email = filterEmail;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterOutcome !== 'all') params.outcome = filterOutcome;
      const [data, sum] = await Promise.all([fetchAuditLog(params), fetchAuditSummary()]);
      setLogs(data);
      setSummary(sum);
    } catch { toast.error('Error al cargar bitácora'); }
    finally { setLoading(false); }
  }, [filterAction, filterEmail, filterDateFrom, filterDateTo, filterOutcome]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setFilterAction('all'); setFilterEmail(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterOutcome('all'); };
  const hasFilters = filterAction !== 'all' || filterEmail || filterDateFrom || filterDateTo || filterOutcome !== 'all';

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Total Eventos" value={summary.total || 0} icon={ScrollText} compact testId="audit-kpi-total" />
        <KPICard label="Logins Exitosos" value={summary.successful_logins || 0} icon={LogIn} compact testId="audit-kpi-logins" />
        <KPICard label="Eventos Exitosos" value={summary.success || 0} icon={ShieldCheck} compact testId="audit-kpi-success" />
        <KPICard label="Eventos Fallidos" value={summary.failed || 0} icon={AlertTriangle} className={(summary.failed || 0) > 0 ? 'border-red-200' : ''} compact testId="audit-kpi-failed" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="Tipo de evento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los eventos</SelectItem>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterOutcome} onValueChange={setFilterOutcome}>
          <SelectTrigger className={`w-[130px] h-8 text-xs ${filterOutcome === 'failed' ? 'border-red-300' : ''}`}><SelectValue placeholder="Resultado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="success">Exitosos</SelectItem>
            <SelectItem value="failed">Fallidos</SelectItem>
          </SelectContent>
        </Select>
        <Input className="h-8 w-[180px] text-xs" placeholder="Buscar por email..." value={filterEmail} onChange={e => setFilterEmail(e.target.value)} />
        <Input type="date" className="h-8 w-[140px] text-xs" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} title="Desde" />
        <Input type="date" className="h-8 w-[140px] text-xs" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} title="Hasta" />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-400" onClick={clearFilters}>Limpiar filtros</Button>
        )}
      </div>

      {/* Table */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        {loading ? <Skeleton className="h-[400px]" /> : (
          <div className="overflow-auto max-h-[500px]">
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow className="bg-slate-50 sticky top-0 z-10">
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 w-40">Fecha y Hora</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 w-8" />
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Evento</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Usuario</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Detalle</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 w-24">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-sm text-slate-400">Sin registros en este periodo</TableCell></TableRow>
                ) : logs.map((log, i) => {
                  const ac = ACTION_LABELS[log.action] || { label: log.action, cls: 'bg-slate-50 text-slate-600 border-slate-200', icon: '•' };
                  const isFailed = FAILED_ACTIONS.has(log.action);
                  const ts = log.timestamp ? new Date(log.timestamp) : null;
                  const dateStr = ts ? ts.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                  const timeStr = ts ? ts.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                  return (
                    <TableRow key={i} className={isFailed ? 'bg-red-50/20' : ''}>
                      <TableCell className="py-2 px-3">
                        <div className="text-[11px] tabular-nums"><span className="text-slate-600">{dateStr}</span><br /><span className="text-slate-400">{timeStr}</span></div>
                      </TableCell>
                      <TableCell className="py-2 px-1">
                        <div className={`h-2 w-2 rounded-full ${isFailed ? 'bg-red-500' : 'bg-emerald-500'}`} title={isFailed ? 'Fallido' : 'Exitoso'} />
                      </TableCell>
                      <TableCell className="py-2 px-3"><Badge variant="outline" className={`${ac.cls} text-[9px]`}>{ac.label}</Badge></TableCell>
                      <TableCell className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <UserAvatar email={log.email} name={log.email} size="xs" />
                          <span className="text-[11px] text-slate-600 font-medium">{log.email || '—'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3 text-[11px] text-slate-500 max-w-[280px] truncate" title={log.detail || ''}>{log.detail || '—'}</TableCell>
                      <TableCell className="py-2 px-3 text-[10px] tabular-nums text-slate-400">{log.ip || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <p className="text-[10px] text-slate-400 text-right">{logs.length} evento{logs.length !== 1 ? 's' : ''} mostrado{logs.length !== 1 ? 's' : ''}</p>
    </div>
  );
}


const MODULE_LABELS = {
  dashboard: 'Dashboard', viviendas: 'Viviendas', pagos: 'Pagos',
  avance_obra: 'Avance de Obra', egresos: 'Egresos', credito: 'Crédito',
  prestamos: 'Préstamos', flujo_caja: 'Flujo de Caja', validacion: 'Validación',
  catalogo_obra: 'Catálogo de Obra', configuracion: 'Configuración', usuarios: 'Usuarios',
  estimaciones: 'Estimaciones',
};
const ACTION_LABELS_SHORT = {
  view: 'Ver', edit: 'Editar', admin: 'Admin',
};

function PermissionsMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const raw = await fetchPermissionMatrix();
      // Ensure every role has all modules (even if empty)
      const matrix = {};
      for (const [role, perms] of Object.entries(raw.matrix || {})) {
        matrix[role] = {};
        for (const mod of (raw.modules || [])) {
          matrix[role][mod] = perms[mod] || [];
        }
      }
      setData({ ...raw, matrix });
    }
    catch { toast.error('Error al cargar permisos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePerm = (mod, action) => {
    if (!selectedRole || !data) return;
    const current = data.matrix[selectedRole]?.[mod] || [];
    const updated = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
    setData(prev => ({
      ...prev,
      matrix: { ...prev.matrix, [selectedRole]: { ...prev.matrix[selectedRole], [mod]: updated } },
    }));
  };

  const handleSave = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      // Ensure all modules are included (empty array for unchecked ones)
      const perms = {};
      for (const mod of modules) {
        perms[mod] = data.matrix[selectedRole]?.[mod] || [];
      }
      await updateRolePermissions(selectedRole, perms);
      toast.success(`Permisos de ${selectedRole} actualizados`);
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-[400px] rounded-xl" />;
  if (!data) return null;

  const roles = data.roles || [];
  const modules = data.modules || [];
  const actions = data.actions || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={selectedRole} onValueChange={setSelectedRole}>
          <SelectTrigger className="w-[220px] h-9"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
          <SelectContent>
            {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.label} — {r.description}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedRole && (
          <Button onClick={handleSave} disabled={saving} size="sm">{saving ? 'Guardando...' : 'Guardar Permisos'}</Button>
        )}
      </div>

      {selectedRole && data.matrix[selectedRole] ? (
        <Card className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-auto">
            <Table data-testid="permissions-matrix">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-4 sticky left-0 bg-slate-50 z-10 min-w-[160px]">Módulo</TableHead>
                  {actions.map(a => (
                    <TableHead key={a} className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2 text-center w-16">{ACTION_LABELS_SHORT[a]}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map(mod => {
                  const modPerms = data.matrix[selectedRole]?.[mod] || [];
                  return (
                    <TableRow key={mod} className="hover:bg-slate-50/50">
                      <TableCell className="py-2.5 px-4 text-[13px] font-medium text-slate-700 sticky left-0 bg-white z-10">{MODULE_LABELS[mod] || mod}</TableCell>
                      {actions.map(action => {
                        const has = modPerms.includes(action);
                        return (
                          <TableCell key={action} className="py-2.5 px-2 text-center">
                            <button
                              onClick={() => togglePerm(mod, action)}
                              className={`h-6 w-6 rounded-md inline-flex items-center justify-center transition-colors ${has ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'}`}
                              data-testid={`perm-${mod}-${action}`}
                            >
                              {has ? <Check className="h-3.5 w-3.5" /> : <X className="h-3 w-3" />}
                            </button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="rounded-xl border bg-white"><div className="py-12 text-center text-sm text-slate-400">Selecciona un rol para ver y editar sus permisos</div></Card>
      )}
    </div>
  );
}


function UserOverrideDialog({ user: targetUser, onClose }) {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUserPermissions(targetUser.id).then(d => {
      setData(d);
      setOverrides(d.overrides || {});
    }).catch(() => toast.error('Error')).finally(() => setLoading(false));
  }, [targetUser.id]);

  const toggleOverride = (mod, action, type) => {
    // type: 'grant' or 'revoke'
    setOverrides(prev => {
      const modOv = { ...(prev[mod] || { grant: [], revoke: [] }) };
      const list = [...(modOv[type] || [])];
      const idx = list.indexOf(action);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(action);
      modOv[type] = list;
      // Clean: if granting, remove from revoke and vice versa
      if (type === 'grant') modOv.revoke = (modOv.revoke || []).filter(a => a !== action);
      if (type === 'revoke') modOv.grant = (modOv.grant || []).filter(a => a !== action);
      return { ...prev, [mod]: modOv };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserOverrides(targetUser.id, overrides);
      toast.success('Excepciones guardadas');
      onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await clearUserOverrides(targetUser.id);
      toast.success('Excepciones eliminadas');
      onClose();
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  if (loading || !data) return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-3xl"><Skeleton className="h-[400px]" /></DialogContent>
    </Dialog>
  );

  const roleBase = data.role_base || {};
  const ACTIONS_LIST = ['view', 'create', 'edit', 'delete', 'validate', 'approve', 'admin'];
  const MODS = Object.keys(MODULE_LABELS);

  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="user-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#F05A28]" />
            Excepciones — {targetUser.name || targetUser.email}
          </DialogTitle>
          <p className="text-xs text-slate-400">Rol base: {roleLabel(data.role)} · Las excepciones se aplican sobre los permisos del rol.</p>
        </DialogHeader>
        <div className="overflow-auto">
          <Table data-testid="override-matrix">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 sticky left-0 bg-slate-50 z-10 min-w-[150px]">Módulo</TableHead>
                {ACTIONS_LIST.map(a => (
                  <TableHead key={a} className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-1.5 text-center w-14">{ACTION_LABELS_SHORT[a]}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {MODS.map(mod => {
                const basePerms = roleBase[mod] || [];
                const modOv = overrides[mod] || {};
                const grants = modOv.grant || [];
                const revokes = modOv.revoke || [];
                return (
                  <TableRow key={mod}>
                    <TableCell className="py-2 px-3 text-[12px] font-medium text-slate-700 sticky left-0 bg-white z-10">{MODULE_LABELS[mod]}</TableCell>
                    {ACTIONS_LIST.map(action => {
                      const fromRole = basePerms.includes(action);
                      const isGranted = grants.includes(action);
                      const isRevoked = revokes.includes(action);
                      // Effective
                      const effective = (fromRole && !isRevoked) || isGranted;
                      // Visual state
                      let bg, icon;
                      if (isGranted) { bg = 'bg-blue-100 text-blue-600 ring-1 ring-blue-300'; icon = '+'; }
                      else if (isRevoked) { bg = 'bg-red-100 text-red-500 ring-1 ring-red-300'; icon = '−'; }
                      else if (fromRole) { bg = 'bg-emerald-50 text-emerald-500'; icon = '✓'; }
                      else { bg = 'bg-slate-100 text-slate-300'; icon = '×'; }

                      return (
                        <TableCell key={action} className="py-2 px-1.5 text-center">
                          <button
                            onClick={() => {
                              if (fromRole && !isRevoked) toggleOverride(mod, action, 'revoke');
                              else if (isRevoked) toggleOverride(mod, action, 'revoke'); // remove revoke
                              else if (!fromRole && !isGranted) toggleOverride(mod, action, 'grant');
                              else if (isGranted) toggleOverride(mod, action, 'grant'); // remove grant
                            }}
                            className={`h-6 w-6 rounded-md inline-flex items-center justify-center text-[10px] font-bold transition-colors ${bg}`}
                            title={isGranted ? 'Excepción: otorgado' : isRevoked ? 'Excepción: revocado' : fromRole ? 'Del rol (clic para revocar)' : 'Sin permiso (clic para otorgar)'}
                          >
                            {icon}
                          </button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-50 border border-emerald-200 inline-block" /> Del rol</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-100 border border-blue-300 inline-block" /> Excepción otorgada</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-100 border border-red-300 inline-block" /> Excepción revocada</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-100 border border-slate-200 inline-block" /> Sin permiso</span>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {Object.keys(overrides).length > 0 && (
            <Button variant="ghost" size="sm" className="text-[11px] text-slate-400 hover:text-red-500 mr-auto" onClick={handleClear} disabled={saving}>Limpiar excepciones</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar Excepciones'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

